import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultRegistrationTerms,
  DefaultCheckoutTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";
import { faqs, howItWorks, AnalyticsHeader } from "./helpers";

import LogoImage from "public/images/magic/global-logo.png";
import SeatMap3dPreview from "public/images/magic/magic_3d_venue.png";
import BackgroundImage from "public/images/magic/magic_ChooseYourGamesBanner.jpeg";

import type { TeamConfig } from "../types";

const BannerImage =
  "https://ss-leagues.s3.amazonaws.com/NBA/Orlando%20Magic%20Marketing%20Assets/Season-Tickets-Banner-3.jpg";
const StaticSeatMapImage =
  "https://ss-leagues.s3.amazonaws.com/NBA/Orlando%20Magic%20Marketing%20Assets/image%20-%202026-03-31T095633.195.jpeg";

const config: TeamConfig = {
  teamSchema: "orlandomagic_fullseason",
  browserTitle: "Magic Full Season Plans",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Orlando Magic",

  manageCard: true,

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00070-magic-sec",
    mapId: "main",
    sectionNameMapping: {
      FLRN: ["FLOORN"],
      FLRS: ["FLOORS"],
      FLRE: ["FLOORE"],
      FLRW: ["FLOORW"],
      ClubF: ["C-F"],
      ClubE: ["C-E"],
      ClubD: ["C-D"],
      ClubC: ["C-C"],
      ClubB: ["C-B"],
      ClubA: ["C-A"],
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    maxGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 2,
    hideViewTicketsInThankYouPage: true,
    hideAllUpcomingGames: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: 3,
    allowedPaymentFailCount: 3,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-XTHMZHVGB4",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
    AnalyticsHeader,
    trackGoogleAnalyticsOnCheckout: true,
    customAnalyticsConversionEvent: "purchase", // default is 'conversion'
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/magic/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    findGamesBannerImage: BannerImage,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage,
    logoImage: LogoImage.src + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage.src,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      hideOverlayLogo: true,
      showLogoWithTitle: false,
      eventCardMaxWidth: 350, // in pixels
      hideDateAndTime: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    checkoutStatusPage: {
      backgroundColor: "unset",
    },

    gameFiltersSettings: {
      hideSectionFilter: true,
      hideAccessibilityFilter: true,

      hideGameFiltersOnFindGames: true,
    },

    checkoutPage: {
      checkoutDisclaimer:
        "Your Ticket Plan will automatically renew. Next season, you don't have to do a thing for your ticket plan renewal, unless of course, you choose to discontinue your tickets, which will be an option. Each year, you'll be notified of any price or benefit changes and the deadline by which you can elect to opt-out if you choose. An Orlando Magic sales representative will be in touch following your purchase to answer any additional questions and collect your signature on the ticket plan agreement.",
    },
  },

  colors: {
    primary: "#0050B5",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      findGamesTitle: "Season Ticket Plan",
      progressBarText: "Add 1 Plan to continue!",
      bottomBarEventsDescription: "Game Plan",
      purchaseSeatTabTitle: "Choose Plan",
      checkoutZipCodeFailText: (
        <>
          We&apos;re sorry, but the zip code on your billing address is
          currently outside the eligible areas for this offer. For further
          questions, please contact the Orlando Magic at{" "}
          <Link
            href="mailto:help@orlandomagic.com"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
          >
            help@orlandomagic.com
          </Link>
          .
        </>
      ),
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Thank you for your purchase with the Orlando Magic! You will be notified when your tickets are available.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
